import React, { useEffect, useRef, useState, useCallback, useImperativeHandle } from "react";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause, AlertCircle, RefreshCw, Pencil, Trash2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { MediaPlayButton } from "@/components/ui/media-button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { WaveformLoadSkeleton } from "./WaveformLoadSkeleton";
import { generateDecorativePeaks } from "./waveformSkeleton";
import { cssToken } from "@/lib/cssToken";
import {
    clampSeekTime,
    fetchAudioBlobWithProgress,
    getBufferDisplayPercent,
} from "./audioPlayerUtils";

type WaveformPhase = "waiting-duration" | "decorative" | "real";

const PLAYBACK_RATES = [0.5, 1, 2] as const;

function resolveDuration(durationSec: number | undefined, media: HTMLMediaElement): number | null {
    if (durationSec != null && Number.isFinite(durationSec) && durationSec > 0) {
        return durationSec;
    }
    if (Number.isFinite(media.duration) && media.duration > 0) {
        return media.duration;
    }
    return null;
}

function getWaveformSeekDuration(
    waveSurfer: WaveSurfer | null,
    durationSec: number | undefined,
    media: HTMLMediaElement,
): number | null {
    if (waveSurfer) {
        const wsDur = waveSurfer.getDuration();
        if (Number.isFinite(wsDur) && wsDur > 0) {
            return wsDur;
        }
    }
    return resolveDuration(durationSec, media);
}

function resolveAudioSrc(url: string): string {
    try {
        return new URL(url, window.location.href).href;
    } catch {
        return url;
    }
}

export interface AudioPlayerHandle {
    seekTo: (seconds: number) => void;
    play: () => void;
}

interface AudioPlayerProps {
    url: string;
    durationSec?: number;
    title?: string;
    date?: string;
    onDelete?: () => void;
    onRename?: (newName: string) => void;
    className?: string;
    isUploading?: boolean;
    uploadError?: string;
    onRetryUpload?: () => void;
    isRenaming?: boolean;
    isDeleting?: boolean;
    isActive?: boolean;
    onPlay?: () => void;
}

export const AudioPlayer = React.forwardRef<AudioPlayerHandle, AudioPlayerProps>(function AudioPlayer({
    url,
    durationSec: durationSecProp,
    title,
    date,
    onDelete,
    onRename,
    className,
    isUploading,
    uploadError,
    onRetryUpload,
    isRenaming: isRenamingProp,
    isDeleting: isDeletingProp,
    isActive,
    onPlay,
}, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const preloadAudioRef = useRef<HTMLAudioElement>(null);
    const waveSurferRef = useRef<WaveSurfer | null>(null);
    const decoderRef = useRef<WaveSurfer | null>(null);
    const decoderContainerRef = useRef<HTMLDivElement | null>(null);
    const realDecodeStartedRef = useRef(false);
    const hasRealWaveformRef = useRef(false);
    const pendingSeekRef = useRef<number | null>(null);
    const playbackRateRef = useRef(1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [waveformPhase, setWaveformPhase] = useState<WaveformPhase>("waiting-duration");
    const [canPlay, setCanPlay] = useState(false);
    const [duration, setDuration] = useState("0:00");
    const [currentTime, setCurrentTime] = useState("0:00");
    const [bufferPercent, setBufferPercent] = useState(0);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [draftTitle, setDraftTitle] = useState(title ?? "");
    const titleInputRef = useRef<HTMLInputElement>(null);

    const isRemoteUrl =
        url.startsWith("http://") || url.startsWith("https://");

    useEffect(() => {
        setDraftTitle(title ?? "");
    }, [title]);

    useEffect(() => {
        if (isEditingTitle && titleInputRef.current) {
            titleInputRef.current.focus();
            titleInputRef.current.select();
        }
    }, [isEditingTitle]);

    const handleRenameSubmit = () => {
        const trimmed = draftTitle.trim();
        if (trimmed && trimmed !== title && onRename) {
            onRename(trimmed);
        } else {
            setDraftTitle(title ?? "");
        }
        setIsEditingTitle(false);
    };

    const handleRenameKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleRenameSubmit();
        } else if (e.key === "Escape") {
            setDraftTitle(title ?? "");
            setIsEditingTitle(false);
        }
    };

    const formatTime = useCallback((seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, "0")}`;
    }, []);

    const syncWaveSurferTime = useCallback((seconds: number) => {
        const audio = preloadAudioRef.current;
        const ws = waveSurferRef.current;
        if (!audio || !ws) return;
        if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
            ws.setTime(seconds);
            pendingSeekRef.current = null;
        } else {
            pendingSeekRef.current = seconds;
        }
    }, []);

    const applySeek = useCallback((seconds: number) => {
        const audio = preloadAudioRef.current;
        if (!audio) return;
        const t = clampSeekTime(seconds, audio, durationSecProp);
        if (t == null) return;
        audio.currentTime = t;
        syncWaveSurferTime(t);
        setCurrentTime(formatTime(t));
    }, [durationSecProp, formatTime, syncWaveSurferTime]);

    const applyPlay = useCallback(() => {
        if (Boolean(isUploading) || !canPlay) return;
        const ws = waveSurferRef.current;
        if (ws) {
            if (!ws.isPlaying()) {
                void ws.play();
            }
            return;
        }
        const audio = preloadAudioRef.current;
        if (audio && audio.paused) {
            void audio.play().catch(() => undefined);
            onPlay?.();
        }
    }, [canPlay, isUploading, onPlay]);

    const handleWaveformSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const audio = preloadAudioRef.current;
        if (!audio) return;
        const dur = getWaveformSeekDuration(waveSurferRef.current, durationSecProp, audio);
        if (dur == null) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width <= 0) return;
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        applySeek(ratio * dur);
    }, [durationSecProp, applySeek]);

    useImperativeHandle(ref, () => ({
        seekTo: applySeek,
        play: applyPlay,
    }), [applyPlay, applySeek]);

    useEffect(() => {
        if (!containerRef.current || !url) return;

        const audio = preloadAudioRef.current;
        if (!audio) return;

        // Set src imperatively so React re-renders (e.g. on pause) don't re-apply src={url}
        // after WaveSurfer load("", peaks) clears the media element's src attribute.
        const resolvedSrc = resolveAudioSrc(url);
        if (audio.src !== resolvedSrc) {
            audio.src = url;
        }

        const abortController = new AbortController();
        let waveSurfer: WaveSurfer | null = null;

        setWaveformPhase("waiting-duration");
        setCanPlay(false);
        setBufferPercent(0);
        setDuration("0:00");
        setCurrentTime("0:00");
        setIsPlaying(false);
        realDecodeStartedRef.current = false;
        hasRealWaveformRef.current = false;
        pendingSeekRef.current = null;

        const flushPendingSeek = () => {
            const pending = pendingSeekRef.current;
            if (pending == null || !waveSurfer) return;
            const audioEl = preloadAudioRef.current;
            if (!audioEl || audioEl.readyState < HTMLMediaElement.HAVE_METADATA) return;
            waveSurfer.setTime(pending);
            pendingSeekRef.current = null;
        };

        let downloadProgressPercent = 0;

        const updateBuffer = () => {
            setBufferPercent(getBufferDisplayPercent(audio, downloadProgressPercent));
        };

        const setDownloadProgress = (percent: number) => {
            downloadProgressPercent = percent;
            updateBuffer();
        };

        const onLoadedMetadata = () => {
            const resolved = resolveDuration(durationSecProp, audio);
            if (resolved != null) {
                setDuration(formatTime(resolved));
            }
            updateBuffer();
            tryInitWaveSurfer();
            flushPendingSeek();
        };

        const onCanPlay = () => {
            setCanPlay(true);
            updateBuffer();
            startRealWaveformDecode();
        };

        const destroyDecoder = () => {
            if (decoderRef.current) {
                decoderRef.current.destroy();
                decoderRef.current = null;
            }
            if (decoderContainerRef.current) {
                decoderContainerRef.current.remove();
                decoderContainerRef.current = null;
            }
        };

        const startRealWaveformDecode = () => {
            if (
                abortController.signal.aborted ||
                realDecodeStartedRef.current ||
                !waveSurfer
            ) {
                return;
            }
            realDecodeStartedRef.current = true;

            void (async () => {
                try {
                    const blob = await fetchAudioBlobWithProgress(
                        url,
                        abortController.signal,
                        isRemoteUrl ? "include" : "same-origin",
                        setDownloadProgress,
                    );
                    if (abortController.signal.aborted || !waveSurfer) return;

                    const hidden = document.createElement("div");
                    hidden.style.display = "none";
                    document.body.appendChild(hidden);
                    decoderContainerRef.current = hidden;

                    const decoder = WaveSurfer.create({
                        container: hidden,
                        sampleRate: 8000,
                    });
                    decoderRef.current = decoder;

                    decoder.on("ready", async () => {
                        if (abortController.signal.aborted || !waveSurfer) return;
                        try {
                            const realPeaks = decoder.exportPeaks({ maxLength: 2000 });
                            const dur = decoder.getDuration();
                            destroyDecoder();

                            const playing = waveSurfer.isPlaying();
                            hasRealWaveformRef.current = true;
                            const redrawPromise = new Promise<void>((resolve) => {
                                waveSurfer!.once("redrawcomplete", () => resolve());
                            });
                            const t = Math.max(waveSurfer.getCurrentTime(), audio.currentTime || 0);
                            await waveSurfer.loadBlob(blob, realPeaks, dur);
                            if (abortController.signal.aborted) return;
                            await redrawPromise;
                            waveSurfer.setTime(t);
                            if (playing) {
                                void waveSurfer.play();
                            }
                            setWaveformPhase("real");
                            setDuration(formatTime(dur));
                        } catch (err) {
                            // wavesurfer decode — browser media API (see query-exceptions).
                            console.error("[AudioPlayer] real waveform decode failed", err);
                            destroyDecoder();
                        }
                    });

                    decoder.on("error", (err) => {
                        if (abortController.signal.aborted) return;
                        console.error("[AudioPlayer] real waveform decode failed", err);
                        destroyDecoder();
                    });

                    await decoder.loadBlob(blob);
                } catch (err) {
                    // Blob download for waveform peaks — browser media, not HTTP client.
                    if (abortController.signal.aborted) return;
                    console.error("[AudioPlayer] background audio download failed", err);
                    destroyDecoder();
                }
            })();
        };

        const tryInitWaveSurfer = () => {
            if (abortController.signal.aborted || waveSurfer || !containerRef.current) {
                return;
            }

            const resolvedDuration = resolveDuration(durationSecProp, audio);
            if (resolvedDuration == null) return;

            waveSurfer = WaveSurfer.create({
                container: containerRef.current,
                waveColor: cssToken("--color-neutral-text-400", "#d7d5d2"),
                progressColor: cssToken("--color-primary-400", "#3559b8"),
                barWidth: 3,
                barGap: 2,
                barRadius: 3,
                barMinHeight: 3,
                height: 38,
                normalize: true,
                cursorWidth: 0,
                interact: false,
                media: audio,
                url,
                peaks: generateDecorativePeaks(url),
                duration: resolvedDuration,
                ...(isRemoteUrl
                    ? {
                          backend: "MediaElement" as const,
                          fetchParams: { credentials: "include" as const },
                      }
                    : {}),
            });
            waveSurferRef.current = waveSurfer;

            waveSurfer.on("ready", () => {
                if (abortController.signal.aborted || hasRealWaveformRef.current) return;

                waveSurfer!.setPlaybackRate(playbackRateRef.current);
                setWaveformPhase("decorative");
                setDuration(formatTime(waveSurfer!.getDuration()));
                updateBuffer();

                waveSurfer!.once("redrawcomplete", () => {
                    if (abortController.signal.aborted || hasRealWaveformRef.current) return;
                    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
                        startRealWaveformDecode();
                    }
                });
            });
            waveSurfer.on("audioprocess", () => {
                setCurrentTime(formatTime(waveSurfer!.getCurrentTime()));
            });
            waveSurfer.on("timeupdate", (time: number) => {
                setCurrentTime(formatTime(time));
            });
            waveSurfer.on("play", () => {
                setIsPlaying(true);
                onPlay?.();
            });
            waveSurfer.on("pause", () => setIsPlaying(false));
            waveSurfer.on("finish", () => {
                setIsPlaying(false);
                setCurrentTime(formatTime(waveSurfer!.getDuration()));
            });
        };

        audio.addEventListener("loadedmetadata", onLoadedMetadata);
        audio.addEventListener("canplay", onCanPlay);
        audio.addEventListener("progress", updateBuffer);
        audio.addEventListener("timeupdate", updateBuffer);
        audio.addEventListener("seeked", flushPendingSeek);

        if (durationSecProp != null && durationSecProp > 0) {
            setDuration(formatTime(durationSecProp));
            tryInitWaveSurfer();
        }

        if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
            onLoadedMetadata();
        }
        if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
            onCanPlay();
        }

        return () => {
            abortController.abort();
            audio.removeEventListener("loadedmetadata", onLoadedMetadata);
            audio.removeEventListener("canplay", onCanPlay);
            audio.removeEventListener("progress", updateBuffer);
            audio.removeEventListener("timeupdate", updateBuffer);
            audio.removeEventListener("seeked", flushPendingSeek);
            destroyDecoder();
            if (waveSurfer) {
                waveSurfer.destroy();
            }
            waveSurferRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, isRemoteUrl, durationSecProp, formatTime]);

    useEffect(() => {
        if (isActive === false && isPlaying) {
            waveSurferRef.current?.pause();
        }
    }, [isActive, isPlaying]);

    useEffect(() => {
        playbackRateRef.current = playbackRate;
        waveSurferRef.current?.setPlaybackRate(playbackRate);
    }, [playbackRate]);

    const showPlaySpinner = Boolean(isUploading);
    const playDisabled = Boolean(isUploading) || !canPlay;
    const playDimmed = playDisabled;
    const showSkeleton = waveformPhase === "waiting-duration";
    const showBufferOverlay =
        waveformPhase !== "waiting-duration" && bufferPercent < 100;

    const togglePlay = () => {
        if (playDisabled) return;
        waveSurferRef.current?.playPause();
    };

    const cyclePlaybackRate = () => {
        setPlaybackRate((prev) => {
            const idx = PLAYBACK_RATES.indexOf(prev as (typeof PLAYBACK_RATES)[number]);
            const nextIdx = (idx + 1) % PLAYBACK_RATES.length;
            return PLAYBACK_RATES[nextIdx];
        });
    };

    return (
        <div className={cn("group flex flex-col gap-1.5 w-full", className)}>
            <audio
                ref={preloadAudioRef}
                preload="auto"
                className="hidden"
            />
            {/* ── Header: title + delete ── */}
            {(title || onDelete || onRename) && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
                        {isEditingTitle ? (
                            <div className="flex h-10 items-center rounded-s border border-neutral-border-300 pl-3 pr-1 w-full max-w-sm">
                                <Input
                                    ref={titleInputRef}
                                    value={draftTitle}
                                    onChange={(e) => setDraftTitle(e.target.value)}
                                    onKeyDown={handleRenameKeyDown}
                                    disabled={isRenamingProp || isDeletingProp || isUploading}
                                    className="text-body2 font-medium h-8 w-full bg-transparent! px-0 hover:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                />
                                <div className="ml-2 flex items-center gap-1 border-l border-neutral-border-300 pl-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 shrink-0 text-neutral-text-500 hover:text-neutral-text-800"
                                        onClick={handleRenameSubmit}
                                        loading={isRenamingProp}
                                        disabled={isDeletingProp || isUploading}
                                    >
                                        {!isRenamingProp ? <Check size={14} /> : null}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 shrink-0 text-neutral-text-500 hover:text-neutral-text-800"
                                        onClick={() => {
                                            setDraftTitle(title ?? "");
                                            setIsEditingTitle(false);
                                        }}
                                        disabled={isRenamingProp || isDeletingProp || isUploading}
                                    >
                                        <X size={14} />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center text-body2 font-medium! gap-2 text-neutral-text-500">
                                {title && <div className="truncate">{title}</div>}
                                {onRename && !isUploading && (
                                    <IconButton
                                        variant="ghost"
                                        size="xs"
                                        onClick={() => setIsEditingTitle(true)}
                                        disabled={isRenamingProp || isDeletingProp}
                                        className="h-auto p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                                        aria-label="Редактировать название"
                                    >
                                        <Pencil className="size-3 text-neutral-text-500" strokeWidth={2.5} />
                                    </IconButton>
                                )}
                            </div>
                        )}
                    </div>
                    {onDelete && !isEditingTitle && !isUploading && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    disabled={isRenamingProp || isDeletingProp}
                                    className="text-neutral-text-500 hover:text-state-negative shrink-0 text-body2 font-medium opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-all"
                                >
                                    <Trash2 className="size-3" strokeWidth={2.5} />
                                    Удалить
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Удаление аудиозаписи</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Вы действительно хотите удалить аудиозапись из профиля?
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="flex-1">
                                        Отменить
                                    </AlertDialogCancel>
                                    <AlertDialogAction className="flex-1" onClick={onDelete}>
                                        Удалить аудиозапись
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
            )}
            {/* ── Player bar ── */}
            <div className="flex items-center bg-neutral-background-100 rounded-m p-2 gap-2 h-[var(--audio-player-bar-height)]">
                {/* Play / Pause */}
                <MediaPlayButton
                    onClick={togglePlay}
                    disabled={playDisabled}
                    loading={showPlaySpinner}
                    dimmed={playDimmed}
                    aria-label={isPlaying ? "Пауза" : "Воспроизвести"}
                >
                    {isPlaying ? (
                        <Pause size={20} className="text-neutral-text-800" />
                    ) : (
                        <Play size={20} className="ml-0.5 text-neutral-text-800" />
                    )}
                </MediaPlayButton>
                {/* Waveform */}
                <div
                    className="relative flex-1 min-w-0 h-[var(--audio-waveform-height)] flex items-center cursor-pointer"
                    data-testid="waveform-seek"
                    onClick={handleWaveformSeek}
                >
                    {showSkeleton && (
                        <WaveformLoadSkeleton
                            bufferPercent={bufferPercent}
                            seed={url}
                            className="absolute inset-0 z-10 pointer-events-none"
                        />
                    )}
                    <div
                        ref={containerRef}
                        className={cn(
                            "relative w-full",
                            showSkeleton && "opacity-0",
                        )}
                    />
                    {showBufferOverlay && (
                        <div
                            className="absolute inset-y-0 right-0 z-20 pointer-events-none bg-neutral-background-100/90"
                            style={{ width: `${100 - bufferPercent}%` }}
                            aria-hidden
                        />
                    )}
                </div>
                {/* Playback speed */}
                <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={cyclePlaybackRate}
                    disabled={playDisabled}
                    className="h-[var(--audio-rate-btn-height)] min-w-[var(--audio-rate-btn-min-width)] shrink-0 rounded-full bg-neutral-background-200 px-2 text-body2 tabular-nums text-neutral-text-800 transition-transform active:scale-95 hover:bg-neutral-background-200 disabled:active:scale-100 disabled:opacity-50"
                    title="Скорость воспроизведения"
                    aria-label="Скорость воспроизведения"
                >
                    {playbackRate}x
                </Button>
                {/* Duration */}
                <div className="shrink-0 text-body1 text-neutral-text-500 tabular-nums mr-2">
                    {isPlaying ? currentTime : duration}
                </div>
            </div>
            {/* ── Info / Error ── */}
            {uploadError ? (
                <div className="text-caption text-state-negative flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                        <AlertCircle size={12} />
                        {uploadError}
                    </div>
                    {onRetryUpload && (
                        <Button
                            variant="ghost"
                            size="xs"
                            className="h-6 px-2 text-state-negative hover:text-state-negative hover:bg-state-negative/10"
                            onClick={onRetryUpload}
                        >
                            <RefreshCw size={12} className="mr-1" />
                            Повторить
                        </Button>
                    )}
                </div>
            ) : date ? (
                <div className="text-caption text-neutral-text-500">{date}</div>
            ) : null}
        </div>
    );
});
